import React from 'react';
import Container from 'react-bootstrap/Container';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import Loader from "../../loader/Loader";
import Multiselect, {Item} from '../../multiselect/Multiselect';
import {CreateSchemaContext, GenerateActionResult} from "./context/CreateSchemaContext";
import {WizardStepProps} from "../../wizard/Wizard";
import {UnsavedChangesConfirmation} from "../../../common/NavigationConfirmation";
import {CreateSchemaWizardLeavingConfirmationMessage} from "./CreateSchemaWizard";
import {OptionProps, default as SelectMenu} from "../../select/SelectMenu";
import {Tooltip} from "../../../common/tooltip/Tooltip";
import {InfoIcon} from "../../../common/infoicon/InfoIcon";

export type GenerateSchemaModel = {
  id?: string;
  label?: string;
  vocPfx?: string;
  stringMode: string;
  lang?: string;
  files?: Array<any>
};

type GenerateSchemaStepState = {
  id?: string;
  label?: string;
  vocPfx?: string;
  stringMode: string;
  fetch: string;
  validate: string;
  implicit: string;
  ontologies: Array<any>
  selectedOntologies: Array<any>;
  generateStepEnabled: boolean;
}

class GenerateSchemaStep extends React.Component<WizardStepProps, GenerateSchemaStepState> {

  static contextType = CreateSchemaContext;

  constructor(props: WizardStepProps) {
    super(props);
    this.state = {
      vocPfx: '',
      id: '',
      label: '',
      stringMode: 'stringOrLangString',
      fetch: '',
      validate: '',
      implicit: '',
      ontologies: [],
      selectedOntologies: [],
      generateStepEnabled: false
    };
    this.handleInputChange = this.handleInputChange.bind(this);
    this.selectionChange = this.selectionChange.bind(this);
    this.stringModeChange = this.stringModeChange.bind(this);
  }

  componentDidMount() {
    const selectedOntologies = this.createSelectedItemsModel();
    const generateStepEnabled = selectedOntologies && selectedOntologies.length > 0;
    this.setState({
      generateStepEnabled,
      selectedOntologies
    });
  }

  /**
   * Next step should be forbidden until at least one ontology is selected.
   */
  onGenerate = () => {
    if (this.state.generateStepEnabled) {
      const payload: GenerateSchemaModel = this.getSchemaGeneratePayload();
      this.context.generateSchema(payload).then((result: GenerateActionResult) => {
        if (result.success && !result.hasError) {
          this.context.removeError();
        }
        return this.props.next(result.success);
      });
    }
  };

  itemsFilter = (items: Array<Item>, newItem: Item): Array<Item> => {
    return items.filter(item => item.value.filename !== newItem.value.filename)
  };

  createItemsModel(): Array<Item> {
    // If a single file is uploaded, then we should auto-select it, so we put it in the
    // selectedOntologies list.
    if (this.context.files.length === 1) {
      return [];
    }
    return this.buildItemsModel();
  }

  createSelectedItemsModel(): Array<Item> {
    // If multiple files are uploaded, then we put all of them in the ontologies list.
    if (this.context && this.context.files.length > 1) {
      return [];
    }
    return this.buildItemsModel();
  }

  buildItemsModel() {
    return this.context.files.map((file: any) => {
      return {label: file.originalname, value: file};
    });
  }

  handleInputChange(event: any) {
    const target = event.target;
    const value: string = target.value;
    const id: string = target.id;
    const state: any = {};
    state[id] = value;
    this.setState(state);
  }

  selectionChange(items: any) {
    const generateStepEnabled = items && items.length > 0;
    this.setState({
      selectedOntologies: [...items],
      generateStepEnabled
    });
  }

  stringModeChange(stringMode: string) {
    const state: any = {
      stringMode
    };
    // When stringMode is 'string', then 'lang' attributes are inapplicable.
    if (stringMode === 'string') {
      state.fetch = '';
      state.validate = '';
      state.implicit = '';
    }
    this.setState(state);
  }

  onStepBack = () => {
    this.context.removeError();
    return this.props.prev();
  };

  private getLangStringConfig() {
    // combine arguments to a string which to be used as a 'lang' parameter during generation
    //fetch: en~,de,ANY, validate: en~,de~,fr~, implicit: en-GB
    const fetch = this.state.fetch;
    const validate = this.state.validate;
    const implicit = this.state.implicit;
    const config: Array<string> = [];
    fetch && config.push(`fetch: ${fetch}`);
    validate && config.push(`validate: ${validate}`);
    implicit && config.push(`implicit: ${implicit}`);
    return config.length ? config.join(', ') : '';
  }

  private getSchemaGeneratePayload(): GenerateSchemaModel {
    const files = this.state.selectedOntologies.map(item => item.value);
    const stringMode = this.state.stringMode;
    const lang = this.getLangStringConfig();
    const payload: GenerateSchemaModel = {
      files,
      vocPfx: this.state.vocPfx,
      id: this.state.id,
      label: this.state.label,
      stringMode
    };
    if (lang) {
      payload.lang = lang;
    }
    return payload;
  }

  render() {
    const loader = this.context.isRequestRunning ? <Loader/> : false;
    const langGroupDisabled: boolean = this.state.stringMode === 'string';
    return (
      <>
      <Container className="GenerateSchemaStep no-padding" fluid>
        <Row className="pb-3">
          <Col>
            <div className="StepActions">
              <Button className="prev-btn"
                      onClick={this.onStepBack}
                      disabled={this.context.isRequestRunning}
                      title="Upload Ontologies">
                Upload
              </Button>
              <Button className="generate-schema next-btn"
                      disabled={!this.state.generateStepEnabled || this.context.isRequestRunning}
                      onClick={this.onGenerate}
                      title="Generate Schema">
                Generate
              </Button>
              {loader}
            </div>
          </Col>
        </Row>
        <Row className="pb-3">
          <Col>
            <div className="OntologiesSelectorWrapper border p-3">
              <h4>Select Ontologies</h4>
              <Multiselect items={this.createItemsModel()}
                           selectedItems={this.createSelectedItemsModel()}
                           filter={this.itemsFilter}
                           onChange={this.selectionChange}/>
            </div>
          </Col>
          <Col>
            <Form className="ParamsForm border p-3">
              <h4>Parameters</h4>
              <Form.Group controlId="vocPfx" as={Row}>
                <Form.Label column sm={3}>Vocabulary prefix<InfoIcon tooltip="VocabPrefixDescription"/></Form.Label>
                <Col sm={9}>
                  <Form.Control type="text" value={this.state.vocPfx}
                                onChange={this.handleInputChange}
                                autoComplete="off"/>
                </Col>
              </Form.Group>
              <Form.Group controlId="id" as={Row}>
                <Form.Label column sm={3}>Schema ID<InfoIcon tooltip="SchemaIdDescription"/></Form.Label>
                <Col sm={9}>
                  <Form.Control type="text" value={this.state.id}
                                onChange={this.handleInputChange}
                                autoComplete="off"/>
                </Col>
              </Form.Group>
              <Form.Group controlId="label" as={Row}>
                <Form.Label column sm={3}>Label<InfoIcon tooltip="LabelDescription"/></Form.Label>
                <Col sm={9}>
                  <Form.Control type="text" value={this.state.label}
                                onChange={this.handleInputChange}
                                autoComplete="off"/>
                </Col>
              </Form.Group>
              <Form.Group controlId="stringMode" as={Row}>
                <Form.Label column sm={3}>String Mode<InfoIcon tooltip="StringModeDescription"/></Form.Label>
                <Col sm={9}>
                  <StringModeSelectMenu stringMode={this.state.stringMode}
                                        onChange={this.stringModeChange}/>
                </Col>
              </Form.Group>
              <Card className="LangGroup mt-4">
                <Card.Body>
                  <Card.Title>Default langString configurations<InfoIcon
                    tooltip="LangStringConfigDescription"/></Card.Title>
                  <Card.Text as="div">
                    <Form.Group controlId="fetch" as={Row}>
                      <Form.Label column sm={3}>Fetch</Form.Label>
                      <Col sm={9}>
                        <Form.Control type="text" value={this.state.fetch}
                                      onChange={this.handleInputChange}
                                      disabled={langGroupDisabled}
                                      placeholder="en~,de,ANY"
                                      autoComplete="off"/>
                      </Col>
                    </Form.Group>
                    <Form.Group controlId="validate" as={Row}>
                      <Form.Label column sm={3}>Validate</Form.Label>
                      <Col sm={9}>
                        <Form.Control type="text" value={this.state.validate}
                                      onChange={this.handleInputChange}
                                      disabled={langGroupDisabled}
                                      placeholder="en~,de~,fr~"
                                      autoComplete="off"/>
                      </Col>
                    </Form.Group>
                    <Form.Group controlId="implicit" as={Row}>
                      <Form.Label column sm={3}>Implicit</Form.Label>
                      <Col sm={9}>
                        <Form.Control type="text" value={this.state.implicit}
                                      onChange={this.handleInputChange}
                                      disabled={langGroupDisabled}
                                      placeholder="en-GB"
                                      autoComplete="off"/>
                      </Col>
                    </Form.Group>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Form>
          </Col>
        </Row>
      </Container>
      <UnsavedChangesConfirmation when={true}
                                  message={CreateSchemaWizardLeavingConfirmationMessage}/>
      <GenerationMessages error={this.context.error}/>
      <VocabPrefixDescription/>
      <SchemaIdDescription/>
      <LabelDescription/>
      <StringModeDescription/>
      <LangStringConfigDescription/>
      </>
    );
  }
}

function GenerationMessages(props: any) {
  if (!props.error) {
    return null;
  }
  if (props.error instanceof Array) {
    return <Alert variant="danger" className="SchemaGenerateError">
      <ul>{
        props.error.map((error: any, index: number) => <li key={index}>{error.message}</li>)
      }</ul>
    </Alert>
  }
  return <Alert variant="danger"
                className="SchemaGenerateError">{JSON.stringify(props.error)}</Alert>
}

function StringModeSelectMenu(props: { stringMode: string, onChange: (selection: string) => void }) {
  const options: Array<OptionProps> = [
    {id: 'stringOrLangString', label: 'String or LangString'},
    {id: 'langString', label: 'LangString'},
    {id: 'string', label: 'String'}
  ];
  return (
    <SelectMenu className="StringModeSelectMenu"
                options={options}
                selected={props.stringMode}
                onChange={props.onChange}
                standalone={false}/>
  );
}

function StringModeDescription() {
  return (
    <Tooltip target="StringModeDescription">
      <div>
        <p>This parameter controls the way of emitting string-related datatypes</p>
        <table className="table">
          <thead>
          <tr>
            <th scope="col">Datatype</th>
            <th scope="col">stringOrLangString(default)</th>
            <th scope="col">langString</th>
            <th scope="col">string</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>rdf:langString</td>
            <td>langString</td>
            <td>langString</td>
            <td>string</td>
          </tr>
          <tr>
            <td>rdf:PlainLiteral, rdfs:Literal, schema:Text, undefined</td>
            <td>stringOrLangString</td>
            <td>langString</td>
            <td>string</td>
          </tr>
          <tr>
            <td>xsd:string</td>
            <td>string</td>
            <td>string</td>
            <td>string</td>
          </tr>
          </tbody>
        </table>
      </div>
    </Tooltip>
  )
}

function LangStringConfigDescription() {
  return <Tooltip target="LangStringConfigDescription">
    <p>This parameter will generate default values for the langString properties used in the schema.<br/>It is optional and
      can be skipped or the values can be added later in the config section of the schema.</p>
    <ul>
      <li>Supported formats</li>
      <ul>
        <li>Set only fetch order - Example: en~,de,ANY</li>
        <li>Set fetch/validate/implicit using key value format - Example: fetch: en~,de,ANY, validate: en~,de~,fr~,
          implicit: en-GB
        </li>
      </ul>
      <li>Configurations meaning</li>
      <ul>
        <li><strong>Fetch</strong> - restricts the returned languages, the preferred order. Obtain the first value
          according to the order, or all matching values.
        </li>
        <li><strong>Validate</strong> - validate values during create/update (mutation) to restrict certain languages or
          ensure uniqueness per language.
        </li>
        <li><strong>Implicit</strong> - allows to omit the language tag on mutation.</li>
      </ul>
    </ul>
  </Tooltip>
}

function VocabPrefixDescription() {
  return <Tooltip target="VocabPrefixDescription">
    <p>Specifies a <strong>vocPfx</strong> for the SOML.<br/>The namespace for this prefix should be declared in
      one
      of the ontology files.<br/>If not specified explicitly, one will be extracted automatically.<br/>Setting it to
      NONE will
      set a default namespace <em>http://example.com/</em>. Use this option if there is no main ontology to be used.</p>
  </Tooltip>
}

function SchemaIdDescription() {
  return <Tooltip target="SchemaIdDescription">
    <p>Specifies an ID for the SOML.<br/>Optional unless <strong>vocPfx</strong> is set to <strong>NONE</strong>. In
      that
      case there is no other way to determine the SOML id as no main ontology will be used.</p>
  </Tooltip>
}

function LabelDescription() {
  return <Tooltip target="LabelDescription">
    <p>Specifies a label for the SOML.<br/>This is to be used only with the id parameter. Otherwise the label will be
      extracted from the main ontology automatically.</p>
  </Tooltip>
}

export default GenerateSchemaStep;
